import { RestApiClient } from 'ifield-core-components'
import log from 'loglevel'
import _ from 'lodash'
import { FUNCTION_TYPE } from '@/constants'

/**
 * センサー取得
 * @param {Number} id ID
 * @returns {Array.<Object>|Object} データ
 */
const getSensors = async (id) => {
  const endPoint = id == null ? 'sensor' : `sensor/${id}`
  const url = RestApiClient.createApiUrl(endPoint)
  const json = await RestApiClient.executeGetApi(url)
  return json.data
}

/**
 * 初期スロットル間隔(ms)
 */
const INITIAL_THROTTLE_TIME = 30000

/**
 * 当モジュールで扱うデータが利用可能な権限タイプ
 */
const MY_FUNCTION_TYPE = FUNCTION_TYPE.SENSOR

export default {
  namespaced: true,
  state: {
    sensors: [],
    throttleTime: INITIAL_THROTTLE_TIME,
    throttler: null,
  },
  persistentStates: ['throttleTime'],
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      state.sensors = []
      state.throttleTime = INITIAL_THROTTLE_TIME
      state.throttler = null
    },
    /**
     * センサーを設定する
     * @param {*} state
     * @param {Array.<Object>} sensors センサー
     */
    setSensors(state, sensors) {
      state.sensors = sensors
    },
    /**
     * 特定のセンサーを設定する
     * @param {*} state
     * @param {Object} sensor センサー
     */
    setSpecificSensor(state, sensor) {
      const targetIndex = state.sensors.findIndex((l) => l.id === sensor.id)
      if (targetIndex >= 0) {
        state.sensors[targetIndex] = sensor
      } else {
        state.sensors.push(sensor)
      }
    },
    /**
     * スロットル間隔を保存する
     * @param {*} state
     * @param {Number} time スロットル間隔(ms)
     */
    setThrottleTime(state, time) {
      state.throttleTime = +time
    },
    /**
     * スロットル関数を保存する
     * @param {*} state
     * @param {Function} throttler スロットル関数
     */
    setThrottler(state, throttler) {
      state.throttler = throttler
    },
  },
  getters: {
    /**
     * センサー
     * @param {*} state
     * @returns {Array.<Object>}
     */
    sensors: (state) => state.sensors,
    /**
     * センサー(Map)
     * @param {*} state
     * @returns {Map}
     */
    sensorMap: (state) =>
      new Map(state.sensors.map((sensor) => [sensor.id, sensor])),
    /**
     * 特定IDのセンサーを取得
     * @param {*} state
     * @param {*} getters
     * @returns {Function} IDを引数に特定IDのセンサーを返す関数
     */
    getSensorById: (state, getters) => (id) => getters.sensorMap.get(id),
    /**
     * プレイスIDに紐付いているセンサーを取得する
     * @param {*} state
     * @returns {Function} プレイスIDを引数に紐付いているセンサーデータを返す関数
     */
    getSensorsByPlaceId: (state) => (placeId) => {
      return state.sensors.filter((s) => s.place_id === +placeId)
    },
    /**
     * ユーザーIDに紐付いているセンサーを取得する
     * @param {*} state
     * @returns {Function} ユーザーIDを引数に紐付いているセンサーデータを返す関数
     */
    getSensorsByUserId: (state) => (userId) => {
      return state.sensors.filter((s) => s.user_id === +userId)
    },
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * Sensorを更新する
     */
    async updateSensors({ commit, rootGetters }) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: sensor is unavaliable. skip update')
        return
      }
      const sensors = await getSensors()
      commit('setSensors', sensors)
    },
    /**
     * 特定のSensorを更新する
     * @param {Number} id 対象のセンサーID
     */
    async updateSpecificSensor({ commit, rootGetters }, id) {
      if (!rootGetters['functionType'](MY_FUNCTION_TYPE)) {
        log.info('function type: sensor is unavaliable. skip update')
        return
      }
      const sensor = await getSensors(id)
      commit('setSpecificSensor', sensor)
      return sensor
    },
    /**
     * センサーを更新する(スロットル)
     */
    async updateSensorsWithThrottle({ state, commit, dispatch }) {
      if (!state.throttler) {
        const throttler = _.throttle(
          () => dispatch('updateSensors'),
          state.throttleTime,
          { trailing: false }
        )
        commit('setThrottler', throttler)
      }
      return state.throttler()
    },
    /**
     * スロットル間隔更新
     * @param {Number} time スロットル間隔(ms)
     */
    updateThrottleTime({ commit }, time) {
      commit('setThrottleTime', +time)
      commit('setThrottler', null)
    },
  },
}
